// File-upload widget for transaction image/document columns. The column
// stores the served URL of the uploaded file (a plain string). Inline it
// shows a thumbnail (image) or a file chip (document); clicking opens a
// dialog to upload, replace, or remove the file. Surface-agnostic: the
// actual upload transport is injected via `uploadFn` so live preview,
// deployed sites and the standalone runtime can each point it at their own
// endpoint.

import { useEffect, useRef, useState } from 'react'
import { FileText, ExternalLink, Trash2, Upload, X, RefreshCw, Image as ImageIcon } from 'lucide-react'
import { detectFileKind, displayNameForUrl } from './fileKind'

export interface UploadResult {
  url: string
  name?: string
}

export type PreviewMode = 'auto' | 'image' | 'pdf' | 'text' | 'none'

// The classifier and the name reader live in `fileKind` so the Assistant's
// result table can reuse them without importing this widget.
export { detectFileKind, displayNameForUrl } from './fileKind'

// Resolve the effective inline viewer for a file given the author's choice.
function resolvePreviewKind(
  mode: PreviewMode, widgetKind: 'image' | 'document', url: string,
): 'image' | 'pdf' | 'text' | 'none' {
  if (mode === 'none') return 'none'
  if (mode === 'image' || mode === 'pdf' || mode === 'text') return mode
  // auto
  const detected = detectFileKind(url)
  if (detected !== 'other') return detected
  return widgetKind === 'image' ? 'image' : 'none'
}

// Optional context routed to the upload transport so the file is stored under
// the right document type / record folder. Transaction forms leave this unset
// (the server finalizes the folder on save); the smart-page file-upload block
// supplies a resolved docType + recordId so the file lands in place at once.
export interface UploadMeta {
  docType?: string
  recordId?: string
  /** The value being replaced. When the column is backed by a document
   *  repository this is how the server recognises a REPLACEMENT and files it
   *  as the next version of the same document rather than starting a new one.
   *  Ignored by the flat document store. */
  replaces?: string
}

interface Props {
  kind: 'image' | 'document'
  value: string
  onChange: (url: string) => void
  uploadFn: (file: File, meta?: UploadMeta) => Promise<UploadResult>
  accept: string
  disabled?: boolean
  fieldKey?: string
  // When set, forwarded to uploadFn so the file is stored under this document
  // type / record id (used by the smart-page upload block).
  docType?: string
  recordId?: string
  // Inline preview of the current file (image/pdf/text). Defaults to 'none' so
  // transaction-form columns keep their compact chip; the smart-page upload
  // block passes 'auto' (or an explicit mode) to show the file in place.
  previewMode?: PreviewMode
}

export default function FileUploadField({
  kind, value, onChange, uploadFn, accept, disabled, fieldKey, docType, recordId,
  previewMode = 'none',
}: Props) {
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const isImage = kind === 'image'
  const hasFile = !!(value && value.trim())
  const name = displayNameForUrl(value)

  const pickFile = () => { if (!disabled) inputRef.current?.click() }

  const handleFiles = async (files: FileList | null) => {
    const file = files?.[0]
    if (!file) return
    setBusy(true)
    setError(null)
    try {
      const replaces = (value || '').trim()
      const meta = (docType || recordId || replaces)
        ? { docType, recordId, replaces: replaces || undefined }
        : undefined
      const res = await uploadFn(file, meta)
      onChange(res.url)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Upload failed')
    } finally {
      setBusy(false)
      if (inputRef.current) inputRef.current.value = ''
    }
  }

  const remove = () => { onChange(''); setError(null) }

  return (
    <div data-livepreview-field={fieldKey}>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        style={{ display: 'none' }}
        onChange={e => handleFiles(e.target.files)}
      />

      {/* Inline display */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {hasFile && isImage && (
          <button
            type="button"
            onClick={() => setOpen(true)}
            title="View / change image"
            style={{
              padding: 0, border: '1px solid var(--color-border)', borderRadius: 6,
              overflow: 'hidden', cursor: 'pointer', background: 'var(--color-surface-2, var(--color-bg-secondary))',
              width: 44, height: 44, flex: '0 0 44px',
            }}
          >
            <img
              src={value}
              alt={name}
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
          </button>
        )}
        {hasFile && !isImage && (
          <button
            type="button"
            onClick={() => setOpen(true)}
            title="View / change document"
            style={{
              display: 'flex', alignItems: 'center', gap: 8, maxWidth: '100%',
              padding: '6px 10px', border: '1px solid var(--color-border)', borderRadius: 6,
              background: 'var(--color-surface-2, var(--color-bg-secondary))', color: 'var(--color-text)',
              cursor: 'pointer', fontSize: '0.9375rem',
            }}
          >
            <FileText size={15} style={{ flex: '0 0 auto', color: 'var(--color-accent)' }} />
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {name}
            </span>
          </button>
        )}
        {!hasFile && (
          <button
            type="button"
            onClick={() => setOpen(true)}
            disabled={disabled || busy}
            className="btn btn-ghost btn-sm"
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '7px 12px', border: '1px dashed var(--color-border)', borderRadius: 6,
              background: 'var(--color-surface-2, var(--color-bg-secondary))', color: 'var(--color-text-secondary)',
              cursor: disabled ? 'not-allowed' : 'pointer', fontSize: '0.8625rem',
            }}
          >
            {isImage ? <ImageIcon size={15} /> : <Upload size={15} />}
            {busy ? 'Uploading…' : isImage ? 'Add image' : 'Add document'}
          </button>
        )}
        {hasFile && !disabled && (
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="btn btn-ghost btn-sm"
            title="Change"
            style={{
              padding: '6px 8px', border: '1px solid var(--color-border)', borderRadius: 6,
              background: 'transparent', color: 'var(--color-text-secondary)', cursor: 'pointer',
            }}
          >
            <RefreshCw size={13} />
          </button>
        )}
      </div>

      {/* Inline preview (image / pdf / text) below the chip. */}
      {hasFile && (
        <InlinePreview kind={resolvePreviewKind(previewMode, kind, value)} url={value} name={name} />
      )}

      {error && !open && (
        <div style={{ color: '#ef4444', fontSize: '0.8125rem', marginTop: 4 }}>{error}</div>
      )}

      {/* Manage dialog */}
      {open && (
        <div
          onClick={() => setOpen(false)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              width: 420, maxWidth: '90vw', background: 'var(--color-bg)',
              border: '1px solid var(--color-border)', borderRadius: 10,
              boxShadow: '0 10px 40px rgba(0,0,0,0.3)', padding: 18,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
              <div style={{ fontWeight: 600, fontSize: '1.0125rem', color: 'var(--color-text)' }}>
                {isImage ? 'Image' : 'Document'}
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--color-text-secondary)' }}
              >
                <X size={18} />
              </button>
            </div>

            {/* Current file preview */}
            <div
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                minHeight: 120, padding: 12, marginBottom: 14, borderRadius: 8,
                border: '1px solid var(--color-border)', background: 'var(--color-bg-secondary)',
              }}
            >
              {hasFile ? (
                isImage ? (
                  <img
                    src={value}
                    alt={name}
                    style={{ maxWidth: '100%', maxHeight: 200, objectFit: 'contain', borderRadius: 6 }}
                  />
                ) : (
                  <a
                    href={value}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--color-accent)', textDecoration: 'none' }}
                  >
                    <FileText size={20} />
                    <span style={{ wordBreak: 'break-all' }}>{name}</span>
                    <ExternalLink size={14} />
                  </a>
                )
              ) : (
                <span style={{ color: 'var(--color-text-secondary)', fontSize: '0.9625rem' }}>
                  No file uploaded
                </span>
              )}
            </div>

            {error && (
              <div style={{ color: '#ef4444', fontSize: '0.9125rem', marginBottom: 10 }}>{error}</div>
            )}

            {/* Actions: upload / replace, open, remove */}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={pickFile}
                disabled={disabled || busy}
                className="btn btn-primary btn-sm"
                style={{ display: 'flex', alignItems: 'center', gap: 6 }}
              >
                <Upload size={14} />
                {busy ? 'Uploading…' : hasFile ? 'Replace file' : 'Upload a file'}
              </button>
              {hasFile && !isImage && (
                <a
                  href={value}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-ghost btn-sm"
                  style={{ display: 'flex', alignItems: 'center', gap: 6, textDecoration: 'none' }}
                >
                  <ExternalLink size={14} /> Open
                </a>
              )}
              {hasFile && !disabled && (
                <button
                  type="button"
                  onClick={remove}
                  className="btn btn-ghost btn-sm"
                  style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#ef4444', marginLeft: 'auto' }}
                >
                  <Trash2 size={14} /> Remove
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// Inline viewer rendered below the file chip. The document-store `raw`
// endpoints serve files same-origin with their correct content-type, so
// <img>/<iframe> render directly and text can be fetched.
function InlinePreview({
  kind, url, name,
}: { kind: 'image' | 'pdf' | 'text' | 'none'; url: string; name: string }) {
  const [text, setText] = useState<string | null>(null)
  const [textErr, setTextErr] = useState(false)

  useEffect(() => {
    if (kind !== 'text') return
    let cancelled = false
    const ctrl = new AbortController()
    setText(null); setTextErr(false)
    fetch(url, { credentials: 'include', signal: ctrl.signal })
      .then((r) => (r.ok ? r.text() : Promise.reject(new Error(String(r.status)))))
      .then((t) => { if (!cancelled) setText(t.slice(0, 100_000)) })
      .catch(() => { if (!cancelled) setTextErr(true) })
    return () => { cancelled = true; ctrl.abort() }
  }, [kind, url])

  if (kind === 'none') return null

  const frame: React.CSSProperties = {
    marginTop: 8, border: '1px solid var(--color-border)', borderRadius: 8,
    overflow: 'hidden', background: 'var(--color-surface-2, var(--color-bg-secondary))',
  }

  if (kind === 'image') {
    return (
      <div style={frame}>
        <img
          src={url}
          alt={name}
          style={{ display: 'block', maxWidth: '100%', maxHeight: 360, objectFit: 'contain', margin: '0 auto' }}
        />
      </div>
    )
  }

  if (kind === 'pdf') {
    return (
      <div style={frame}>
        <iframe
          src={url}
          title={name || 'PDF preview'}
          style={{ width: '100%', height: 480, border: 'none' }}
        />
      </div>
    )
  }

  // text
  return (
    <div style={frame}>
      {textErr ? (
        <div style={{ padding: 10, fontSize: '0.9125rem', color: 'var(--color-text-secondary)' }}>
          Could not load preview.
        </div>
      ) : text == null ? (
        <div style={{ padding: 10, fontSize: '0.9125rem', color: 'var(--color-text-secondary)' }}>
          Loading preview…
        </div>
      ) : (
        <pre style={{
          margin: 0, padding: 10, maxHeight: 360, overflow: 'auto',
          fontSize: '0.9125rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
          color: 'var(--color-text)',
        }}>{text}</pre>
      )}
    </div>
  )
}
